import animation from '@helpers/animation';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset, customScrollbar } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import IconArrowDown from '@img/icons/icon-arrow-down.svg?url';
import Tick from '@img/icons/tick-white.svg?url';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';

const Title = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  ${fonts.helvetica['73-bold-extended']}
  font-size: 12px;
  color: ${brand.grey[20]};
  text-transform: uppercase;
  width: 100%;
  overflow: hidden;
  padding-right: 50px;
  border-bottom: 1px solid transparent;
  transition: border-bottom-color 0.25s ease-in-out;

  @media ${from(Device.TabletLarge)} {
    flex: 0 0 auto;
  }

  [data-theme='border-accordion'] & {
    height: 48px;
    border-bottom-color: ${brand.grey[85]};
    padding: 0 16px;
    padding-right: ${fluid(48, 52)};

    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 16px;
    background: url(${IconArrowDown}) no-repeat right center;
    transform: translateY(0px);
    transition: transform 150ms ${animation.easeInOutCubic};

    [data-theme='border-accordion'] & {
      right: 20px;
    }
  }
`;

const InnerContent = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const HelpIcon = styled.div`
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Button = styled.button.attrs({ type: 'button' })<{ isClear: boolean }>`
  position: absolute;
  top: 50%;
  right: 0;
  ${fonts.helvetica['55-roman']};
  border-radius: 2px;
  font-size: 12px;
  letter-spacing: ${fonts.ls(120)};
  color: ${brand.grey[55]};
  background: ${brand.grey[90]};
  border: 0;
  outline: 0;
  padding: 10px;
  text-transform: uppercase;
  transform: translateY(125%);
  transition: transform 150ms ${animation.easeInOutCubic};

  [data-theme='border-accordion'] & {
    right: 58px;
  }
`;

// eslint-disable-next-line
const List = styled(m.ul).attrs((props: any) =>
  props['data-theme'] === 'border-accordion'
    ? {
        initial: 'hidden',
        animate: props.isActive ? 'visible' : 'hidden',
        variants: {
          hidden: {
            height: 0,
          },
          visible: {
            height: 'auto',
          },
        },
        transition: {
          bounce: 0,
        },
      }
    : {},
)<{ isActive?: boolean }>`
  z-index: 1;
  padding: 0;
  margin: 0;

  [data-theme='default'] & {
    position: absolute;
    top: calc(100% + 1px);
    left: -1px;
    width: calc(100% + 2px);
    max-height: 0;
    overflow: auto;
    border: 1px solid ${brand.black};
    border-top: 0;
    opacity: 0;
    ${customScrollbar()};
    transform: scaleY(0);
    transform-origin: top;
    pointer-events: none;
    transition: opacity 150ms ${animation.easeInOutCubic}, transform 150ms ${animation.easeInOutCubic};

    ${({ isActive }) =>
      isActive
        ? css`
            @media ${until(Device.TabletLarge)} {
              max-height: calc((48px * 6) + 1px);
            }
          `
        : null};

    @media ${from(Device.TabletLarge)} {
      max-height: calc(48px * 8);
    }
  }

  [data-theme='border-accordion'] & {
    // Swap to toggled / accordion mode //
    overflow: hidden;

    @media ${from(Device.TabletLarge)} {
      overflow: auto;
    }
  }
`;

const baseItem = css`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  padding: 0 16px;
  color: ${brand.black};
  ${fonts.helvetica['55-roman']};
  font-size: 12px;
  text-transform: uppercase;
  border-bottom: 1px solid ${brand.grey[85]};
  background: ${brand.white};
  transition: background-color 150ms ${animation.easeInOutCubic};
  user-select: none;

  small {
    margin-left: auto;
    color: ${brand.grey[55]};
  }

  &:hover:not([disabled]) {
    background: ${brand.grey[90]};
    color: ${brand.red.base};
  }
`;

const Link = styled.a`
  ${baseItem};
`;

const Item = styled.button<{ isSelected?: boolean; disabled?: boolean }>`
  ${ButtonReset};
  ${baseItem};

  &:before {
    content: '';
    width: 18px;
    height: 18px;
    border-radius: 2px;
    border: 1px solid ${brand.nearlyBlack};
    margin-right: 10px;
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 20px;
    background: url(${Tick}) no-repeat center;
    width: 12px;
    height: 100%;
    transform: scale(0);
    opacity: 0;
    transition: opacity 150ms ${animation.easeInOutCubic}, transform 150ms ${animation.easeInOutCubic};
  }

  transition: opacity 0.25s ease-in-out;
  [disabled='true'] & {
    opacity: 0.5;
    pointer-events: none;
  }

  span {
    height: auto;

    svg {
      max-height: 25px;
      width: 100%;

      path[data-tm='true'] {
        color: ${brand.white};
      }
    }
  }

  ${({ isSelected, disabled }) =>
    isSelected
      ? css`
          &:before {
            background-color: ${disabled ? brand.grey[75] : brand.red.base};
          }

          &:after {
            transform: scale(1);
            opacity: 1;
          }
        `
      : null};

  ${({ disabled }) =>
    disabled
      ? css`
          color: ${brand.grey[75]};
          cursor: not-allowed;

          svg {
            filter: grayscale(1);
            opacity: 0.5;
          }

          &:before {
            border: 1px solid ${brand.grey[75]};
          }
        `
      : null}
`;

const Dropdown = styled.button.attrs({ type: 'button' })<{ isActive: boolean }>`
  ${ButtonReset};
  position: relative;
  display: flex;
  background: ${brand.white};
  width: 100%;
  padding: 0 16px;
  cursor: pointer;
  transition: background-color 150ms ${animation.easeInOutCubic};
  grid-column: span 4;
  border-top: 1px solid transparent;
  border-color: ${brand.black};

  @media ${from(Device.TabletLarge)} {
    border: 1px solid transparent;
    border-color: transparent;
    max-height: 60vh;
  }

  @media ${from(Device.DesktopSmall)} {
    grid-column: span 3;
  }

  @media ${from(Device.DesktopLarge)} {
    grid-column: span 2;
  }

  &[data-theme='default'] {
    height: 48px;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: -1px;
      width: 3px;
      height: 100%;
      background: ${brand.red.base};
      opacity: 0;
      transform: scaleY(0);
      transform-origin: center center;
      transition: opacity 150ms ${animation.easeInOutCubic}, transform 150ms ${animation.easeInOutCubic};
    }
  }

  &[data-theme='border-accordion'] {
    border: 2px solid ${brand.black};
    flex-direction: column;
    padding: 0;
  }

  ${({ isActive }) =>
    isActive
      ? css`
          background: ${brand.white};
          border-color: ${brand.black};

          &:before {
            opacity: 1;
            transform: scaleY(1);
          }

          ${Title}:before {
            transform: rotate(180deg);
          }

          ${Button} {
            transform: translateY(-50%);
          }

          &[data-theme='default'] {
            ${List} {
              opacity: 1;
              transform: scaleY(1);
              pointer-events: all;
            }
          }
        `
      : null};
`;

const TooltipContainer = styled(m.div).attrs(() => ({
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  variants: {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
    },
  },
}))`
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.4);
`;

const TooltipInner = styled(m.div)<{ 'data-right-offset': number; 'data-flip'?: number }>`
  position: absolute;
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: 12px;
  padding: 20px 16px ${fluid(27, 32)} 16px;
  background-color: ${brand.white};
  border-bottom: 8px solid ${brand.red.base};
  border-radius: 4px;

  svg {
    width: 20px;
  }

  &[data-flip='true'] {
    &::before {
      clip-path: polygon(50% 100%, 0 0, 100% 0);
      bottom: -23px;
      top: unset;
    }
  }

  @media ${from(Device.TabletLarge)} {
    max-width: min(calc(100vw - 30px), 400px);
  }

  &::before {
    content: '';
    position: absolute;
    width: 15px;
    height: 15px;
    clip-path: polygon(100% 0, 0 50%, 100% 100%);
    right: calc(100% - 1px);
    top: 17px;
    background-color: ${brand.white};
  }

  @media ${until(Device.TabletLarge)} {
    width: calc(100dvw - 30px);

    &::before {
      top: -14px;
      clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
      bottom: calc(100% - 1px);
      transform: translateX(-50%);
      ${(props) =>
        props['data-right-offset'] &&
        css`
          right: calc(100dvw - ${props['data-right-offset']}px - 20px);
        `}
    }
  }
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;

  @media ${from(Device.TabletLarge)} {
    padding-right: 28px;
  }
`;

const TooltipTitle = styled.p`
  ${fonts.helvetica['73-bold-extended']}
  font-weight: 700;
  font-size: 14px;
  line-height: 19px;
  margin-bottom: 24px;

  &[data-hide-margin='true'] {
    margin-bottom: 0px;
  }

  span {
    color: ${brand.red.base};
  }

  @media ${until(Device.TabletLarge)} {
    max-width: calc(100% - 30px);
  }
`;

const TooltipCloseContainer = styled.div`
  cursor: pointer;
  position: absolute;
  top: 16px;
  right: 16px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TooltipInnerContent = styled.div`
  ${fonts.helvetica['55-roman']}
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`;

export const FilterDropdownStyles = {
  Dropdown,
  Title,
  Button,
  List,
  Item,
  Link,
  InnerContent,
  HelpIcon,
  TooltipContainer,
  TooltipInner,
  TooltipContent,
  TooltipTitle,
  TooltipInnerContent,
  TooltipCloseContainer,
};
